import { useMsal } from "@azure/msal-react"
import { useEffect, useState } from "react"
import { useStoreState } from "./storeHooks"

export const useUserProfile = () => {
  const authenticatedUser = useStoreState((state) => state.authenticatedUser)
  const { instance } = useMsal()
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!authenticatedUser) {
      return
    }

    const fetchProfilePhoto = async () => {
      try {
        setLoading(true)

        // Get access token for Microsoft Graph API
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: authenticatedUser,
        })

        if (!tokenResponse.accessToken) {
          return
        }

        // Try to fetch profile photo from Microsoft Graph API
        const response = await fetch(
          "https://graph.microsoft.com/v1.0/me/photo/$value",
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          }
        )

        if (response.ok) {
          const blob = await response.blob()
          const photoUrl = URL.createObjectURL(blob)
          setProfilePhotoUrl(photoUrl)
        } else {
          // If no profile photo available, keep null
          setProfilePhotoUrl(null)
        }
      } catch (error) {
        console.log("Profile photo not available:", error)
        setProfilePhotoUrl(null)
      } finally {
        setLoading(false)
      }
    }

    fetchProfilePhoto()

    // Cleanup function to revoke object URL
    return () => {
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser, instance])

  // Get user display name and initials for avatar fallback
  const getUserDisplayName = () => {
    return authenticatedUser?.name || authenticatedUser?.username || "User"
  }

  const getUserInitials = () => {
    const displayName = getUserDisplayName()
    const names = displayName.split(" ")
    if (names.length >= 2) {
      return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`
    }
    return displayName.charAt(0).toUpperCase()
  }

  return {
    profilePhotoUrl,
    loading,
    displayName: getUserDisplayName(),
    initials: getUserInitials(),
    isAuthenticated: !!authenticatedUser,
  }
}
